import { Translations } from './types';
import { operationsTranslations } from './operations';

const en: Translations = {
  webUpdate: {
    title: 'New version %s is available',
    message: 'Save your work. Refresh the app to load the latest version.',
    refresh: 'Refresh and update',
    updating: 'Loading the new version…',
    error: 'The full update could not be downloaded. Check your connection and try again.',
    unavailable: 'The announced version is not available on this server yet. Please try again shortly.',
    download: 'The update could not be downloaded. Your current version is still working. Please try again.',
    activation: 'The downloaded update could not be activated. Please try again.',
    timeout: 'The update is taking too long to download. Please try again shortly.',
  },
  operations: operationsTranslations.en,
  tabs: {
    home: 'Home',
    inventory: 'Inventory',
    stores: 'Stores',
    categories: 'Categories',
    usage: 'Usage',
    admin: 'Admin',
    documents: 'Documents',
    auditLogs: 'Audit Log',
    reports: 'Reports',
    profile: 'My Profile',
  },
  common: {
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: '+ Add',
    new: '+ New',
    error: 'Error',
    validation: 'Validation',
    noAccess: 'No access',
    confirm: 'Confirm',
    del: 'Del',
    open: 'Open',
    change: 'Change',
    namePlaceholder: 'Name',
    descriptionPlaceholder: 'Description (optional)',
    unknownUser: 'Unknown user',
    done: 'Done',
  },
  login: {
    title: 'GOPR Inventory',
    subtitle: 'Mountain Rescue Equipment Management',
    signInWithGoogle: 'Sign in with Google',
    loginFailed: 'Google Login Failed',
    cancelled: 'Google sign-in was cancelled.',
    popupBlocked: 'Popup was blocked by the browser. Please allow popups for this site.',
    unauthorizedDomain: 'This domain is not authorized for Google sign-in. Contact your administrator.',
    onlyGoprAllowed: 'Only @bieszczady.gopr.pl users are allowed to sign in.',
    playServicesUnavailable: 'Google Play Services is not available on this device.',
    signInRequired: 'Google sign-in is required. Please try again.',
    emailLabel: 'Email',
    emailPlaceholder: 'Email address',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Password',
    signInWithEmail: 'Sign in',
    orSignInWithEmail: 'Sign in with email and password',
    hideEmailForm: 'Hide',
    emailLoginFailed: 'Login Failed',
    invalidCredentials: 'Invalid email or password.',
    userNotFound: 'No account found with this email.',
    emailRequired: 'Email address is required.',
    passwordRequired: 'Password is required.',
  },
  home: {
    welcome: 'Welcome,',
    yourPermissions: 'Your Permissions',
    noPermissions: 'No access. Please contact an administrator.',
    permViewInventory: '✓ View inventory and stock levels\n✓ Create usage records',
    permManage: '✓ Manage equipment and stores\n✓ Manage categories',
    permAdmin: '✓ Manage user roles\n✓ Full system access',
    signOut: 'Sign Out',
    language: 'Language',
    guestNotice: 'To request access to the GOPR Inventory application, please contact l.derwich@bieszczady.gopr.pl',
    contactButton: 'Send message',
    contactEmailSubject: 'GOPR Inventory Access Request',
    contactEmailBody: 'Hello,\n\nI would like to request access to the GOPR Inventory application.\n\nBest regards,',
    navigation: {
      usage: 'Usage Records',
      inventory: 'Inventory',
      stores: 'Stores',
      admin: 'Admin',
      documents: 'Documents',
      auditLogs: 'Audit Log',
      reports: 'Reports',
    },
  },
  inventory: {
    itemType: 'Item type',
    disposable: 'Disposable',
    reusable: 'Reusable',
    reusableHint: 'Use is counted without reducing warehouse stock.',
    title: 'Equipment',
    addTitle: 'Add Equipment',
    editTitle: 'Edit Equipment',
    noItems: 'No equipment found.',
    categoryLabel: 'Category',
    unitLabel: 'Unit',
    unitPlaceholder: 'Unit (e.g. pcs)',
    minQuantityLabel: 'Min. quantity',
    minQuantityPlaceholder: 'Min. quantity (default)',
    validationMessage: 'Name, category and unit are required.',
    confirmDelete: 'Delete "%s"?',
  },
  categories: {
    title: 'Categories',
    addTitle: 'Add Category',
    editTitle: 'Edit Category',
    addSubcategoryTitle: 'Add Subcategory',
    noItems: 'No categories found.',
    noInventoryItems: 'No equipment in this category.',
    validationMessage: 'Name is required.',
    confirmDelete: 'Delete category "%s"?',
    hasSubcategoriesError: 'Cannot delete: category has subcategories.',
    cannotAddInventoryError: 'Cannot add equipment: category has subcategories.',
    cannotAddSubcategoryError: 'Cannot add subcategory: category already has equipment.',
    addEquipmentHere: '+ Add Equipment',
    addSubcategoryHere: '+ Add Subcategory',
    parentCategoryLabel: 'Parent category',
    rootCategory: 'None (top level)',
    weightLabel: 'Order',
    weightPlaceholder: 'Order (e.g. 1)',
    requiresActionNumber: 'Require action number',
  },
  stores: {
    title: 'Stores',
    addTitle: 'Add Store',
    editTitle: 'Edit Store',
    noItems: 'No stores found.',
    namePlaceholder: 'Store name',
    nameRequired: 'Store name is required.',
    weightLabel: 'Order',
    weightPlaceholder: 'Order (e.g. 1)',
    stockTitle: '📦 %s — Stock',
    noStock: 'No stock recorded.',
    confirmDelete: 'Delete store "%s"?',
    revealAll: 'Reveal all',
    collapseAll: 'Collapse all',
    setLimits: 'Limits',
    limitsTitle: 'Minimum limits — %s',
    belowLimit: 'Below limit',
    limitsDefaultHint: 'default: %s',
    viewUsageHistory: 'Usage History',
    orderButton: 'Order',
    orderHistory: 'Orders',
    orderCategories: 'Equipment Categories',
    orderEquipment: 'Equipment to order',
    orderSummary: 'Order summary',
    orderSelectCategories: 'Select equipment categories to order.',
    orderNoEquipment: 'No equipment in this category.',
    orderCurrentStock: 'Current stock: %s',
    orderNotes: 'Notes',
    orderNotesPlaceholder: 'Optional order notes',
    orderConfirm: 'Save & generate XLSX',
    orderEmpty: 'Add at least one item with a quantity greater than zero.',
    orderCreated: 'Order saved',
    orderCreatedMessage: 'The XLSX is being generated. It will be emailed and available in order history.',
    orderRefresh: 'Refresh',
    orderAllStores: 'All stores',
    orderAuthor: 'Created by',
    orderStatusPending: 'Generating',
    orderStatusDone: 'Ready',
    orderStatusError: 'Error',
    orderDownload: 'Download XLSX',
    orderNoOrders: 'No orders found.',
  },
  usage: {
    title: 'Usage Records',
    newRecord: 'New Usage Record',
    noItems: 'No usage records.',
    typeLabel: 'Type',
    equipmentLabel: 'Equipment',
    fromStoreLabel: 'From Store',
    toStoreLabel: 'To Store',
    quantityPlaceholder: 'Quantity',
    notePlaceholder: 'Note (optional)',
    qtyLabel: 'Qty',
    fromLabel: 'From',
    toLabel: 'To',
    validationEquipment: 'Equipment and valid quantity are required.',
    validationFromStore: 'Source store is required for take.',
    validationToStore: 'Destination store is required for add.',
    validationTransfer: 'Both source and destination stores are required for transfer.',
    recordCreated: 'Usage record saved',
    recordCreatedMessage: 'The usage record has been created successfully.',
    typeTake: 'TAKE',
    typeAdd: 'ADD',
    typeTransfer: 'TRANSFER',
    typeStocktaking: 'STOCKTAKING',
    wizardStepInfo: 'Basic Info',
    wizardStepCategories: 'Equipment Categories',
    wizardStepEquipment: 'Equipment',
    wizardStepReview: 'Review List',
    wizardStepSummary: 'Summary',
    wizardDateLabel: 'Date',
    wizardTimeLabel: 'Time',
    wizardStoreLabel: 'Store',
    wizardActionNumberLabel: 'Action Number',
    wizardActionNumberPlaceholder: 'e.g. 12/05/2026 (optional)',
    wizardDescriptionLabel: 'Description',
    wizardDescriptionPlaceholder: 'Event description (optional)',
    wizardSelectCategories: 'Select equipment categories used in this action',
    wizardNoEquipment: 'No equipment in this category.',
    wizardReviewItems: 'Equipment used',
    wizardAddItem: 'Add missing equipment',
    wizardSearchPlaceholder: 'Search equipment...',
    wizardNoItemsAdded: 'No equipment added.',
    wizardConfirm: 'Confirm & Save',
    wizardInvalidDate: 'Invalid date format (DD.MM.YYYY) or time format (HH:MM).',
    wizardDateOutOfRange: 'Action date cannot be in the future or older than 30 days.',
    wizardStoreRequired: 'Please select the store the equipment is taken from.',
    wizardBack: 'Back',
    wizardNext: 'Next',
    wizardSummaryDate: 'Date & Time',
    wizardSummaryStore: 'Store',
    wizardSummaryFromStore: 'From Store',
    wizardSummaryToStore: 'To Store',
    wizardSummaryType: 'Operation Type',
    wizardSummaryAction: 'Action Number',
    wizardSummaryDescription: 'Description',
    wizardSummaryItems: 'Equipment Used',
    wizardTypeLabel: 'Operation Type',
    wizardFromStoreLabel: 'From Store',
    wizardToStoreLabel: 'To Store',
    wizardFromStoreRequired: 'Please select the source store.',
    wizardToStoreRequired: 'Please select the destination store.',
    wizardBothStoresRequired: 'Please select both source and destination stores.',
    wizardViewFull: 'Full list',
    wizardViewCategorized: 'Categorized',
    wizardStockAvailable: 'In store: %s',
    wizardExceedsStock: 'Quantity exceeds available stock (%s).',
    wizardStoreStocktakingLabel: 'Store (stocktaking)',
    wizardStocktakingHint: 'Check equipment items to verify and set current quantities',
    wizardActionNumberInfo: 'Action number may be required for some equipment categories.',
    wizardActionNumberFormatError: 'Action number must follow the format: INT/INT/INT (e.g. 12/05/2026).',
    wizardCategoryRequiresActionNumber: 'Action number required',
    sessionItemsLabel: 'Equipment used',
    sessionItemsLabelAdd: 'Equipment added',
    sessionItemsLabelTransfer: 'Equipment transferred',
    sessionItemsLabelStocktaking: 'Equipment verified',
    sessionItemsCount: 'Items: %s',
    storeHistoryTitle: 'Usage — %s',
    createdBy: 'Created by',
    searchByActionNumber: 'Search by action no.',
    searchPlaceholder: 'Action number (e.g. 12/05/2026)',
    searchButton: 'Search',
    searchTitle: 'Results: %s',
    searchNoResults: 'No results for "%s"',
  },
  admin: {
    title: 'Administration',
    noAccess: 'Admin access required',
    changeRole: 'Change Role',
    changeRoleMessage: "Change %s's role to %s?",
    noUsers: 'No users found.',
    createdAt: 'Created',
    lastLoginAt: 'Last login',
    never: 'Never',
    filterAll: 'All',
    userManagement: 'User Management',
    userManagementDesc: 'View users and manage their roles.',
    scheduler: 'Scheduler',
    schedulerDesc: 'Manage automated tasks (e.g. report generation).',
  },
  scheduler: {
    title: 'Scheduler',
    noAccess: 'Admin access required',
    noTasks: 'No scheduled tasks.',
    addTask: '+ Add task',
    editTask: 'Edit task',
    deleteTask: 'Delete',
    deleteConfirm: 'Delete this task?',
    enabledLabel: 'Enabled',
    scheduleLabel: 'Schedule',
    reportTypeLabel: 'Report type',
    recipientsLabel: 'Recipients (email)',
    recipientPlaceholder: 'Recipient email address',
    addRecipient: 'Add recipient',
    periodLabel: 'Period',
    saveTask: 'Save task',
    typeLabel: 'Task type',
    scheduleDaily: 'Daily',
    scheduleWeekly: 'Weekly',
    scheduleMonthly: 'Monthly',
    dayOfWeekLabel: 'Day of week',
    dayOfMonthLabel: 'Day of month',
    weekDaySun: 'Sun',
    weekDayMon: 'Mon',
    weekDayTue: 'Tue',
    weekDayWed: 'Wed',
    weekDayThu: 'Thu',
    weekDayFri: 'Fri',
    weekDaySat: 'Sat',
    periodNone: 'None (no date range)',
    periodLast7Days: 'Last 7 days',
    periodLastWeek: 'Last week (Mon–Sun)',
    periodLastMonth: 'Last month',
    periodLastYear: 'Last year',
    periodCurrentWeek: 'Current week (Mon–today)',
    periodCurrentMonth: 'Current month',
    validationRecipients: 'At least one recipient is required.',
    enabledStatus: 'Active',
    disabledStatus: 'Inactive',
  },
  auditLogs: {
    title: 'Audit Log',
    noAccess: 'Admin access required',
    noLogs: 'No audit log entries.',
    entity: 'Entity',
    action: 'Action',
    user: 'User',
    timestamp: 'Date',
    details: 'Details',
    entityTypes: {
      operations: 'Tasks and batches',
      category: 'Category',
      equipment: 'Equipment',
      store: 'Store',
      usage: 'Usage',
      document: 'Document',
      user: 'User',
    },
    actions: {
      create: 'Create',
      edit: 'Edit',
      delete: 'Delete',
      update: 'Update',
      login: 'Login',
    },
  },
  documents: {
    title: 'Documents',
    addTitle: 'Add Document',
    editTitle: 'Edit Document',
    noItems: 'No documents found.',
    titleLabel: 'Title',
    titlePlaceholder: 'Document title',
    urlLabel: 'PDF Link',
    urlPlaceholder: 'https://...',
    validationMessage: 'Title and PDF link are required.',
    confirmDelete: 'Delete document "%s"?',
    open: 'Open',
  },
  contact: {
    title: 'Contact & Info',
    about: 'About the App',
    aboutDescription: 'GOPR Inventory is a mountain rescue equipment management system developed for the Bieszczady Group of GOPR.',
    contactInfo: 'Contact Information',
    company: 'Organization',
    companyName: 'Bieszczady Group GOPR',
    generalContact: 'General contact',
    technicalContact: 'Technical contact',
    otherProjects: 'Other projects',
    applicationInfo: 'Application Info',
    versionInfo: 'Version: %s',
    changelog: 'Changelog / version history',
  },
  changelog: {
    title: 'Version history',
    intro: 'Expand a version to see new features and fixes. Release notes are maintained in Polish.',
    development: 'In development',
    major: 'Major version %s',
    minorCount: 'Version series: %s',
    releaseCount: 'Releases: %s',
    current: 'Current version',
    empty: 'Version history is not available yet.',
  },
  drawer: {
    contactLabel: 'Contact & Info',
    logout: 'Sign Out',
  },
  reports: {
    title: 'Reports',
    currentQuantityTitle: 'Current Store Quantities',
    currentQuantityDescription: 'Current stock levels for all equipment across all stores.',
    usageRecordsTitle: 'Usage Records',
    usageRecordsDescription: 'Full history of all store operations broken down by equipment.',
    realUsageTitle: 'Real Equipment Usage',
    realUsageDescription: 'Disposable consumption and reusable use counts on separate sheets, grouped by warehouse and period.',
    requestButton: 'Generate report',
    requesting: 'Requesting...',
    recentReports: 'Recent reports',
    noReports: 'No reports requested yet.',
    statusPending: 'Pending',
    statusDone: 'Done',
    statusError: 'Error',
    emailNotice: 'The report will be generated and sent to your email address.',
    requestSuccess: 'Report requested. Check your email shortly.',
    requestError: 'Failed to request the report.',
    dateFromLabel: 'From',
    dateToLabel: 'To',
    datePlaceholder: 'DD.MM.YYYY',
    dateRangeError: 'Invalid date format (DD.MM.YYYY).',
    generatedBy: 'Generated by',
    belowLimitTitle: 'Inventories Below Limit',
    belowLimitDescription: 'List of equipment whose quantity in a store is below the required minimum, grouped by store.',
    refresh: 'Refresh',
    downloadButton: 'Download',
    systemLabel: 'System',
    recipientsLabel: 'Recipients',
  },
  profile: {
    title: 'My Profile',
    firstnameLabel: 'First name',
    firstnamePlaceholder: 'First name',
    lastnameLabel: 'Last name',
    lastnamePlaceholder: 'Last name',
    saveProfile: 'Save profile',
    profileSaved: 'Profile saved.',
    profileSaveError: 'Failed to save profile.',
    firstnameRequired: 'First name is required.',
    changePassword: 'Change password',
    currentPasswordLabel: 'Current password',
    currentPasswordPlaceholder: 'Current password',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    confirmPasswordPlaceholder: 'Confirm new password',
    passwordChanged: 'Password changed successfully.',
    passwordChangeError: 'Failed to change password.',
    passwordMismatch: 'New passwords do not match.',
    currentPasswordRequired: 'Current password is required.',
    newPasswordRequired: 'New password is required.',
    newPasswordTooShort: 'New password must be at least 6 characters.',
    emailPasswordOnly: 'Password change is only available for email/password accounts.',
    setPasswordDescription: 'Set a password to be able to sign in with both Google and email/password.',
    setPassword: 'Set password',
    passwordSet: 'Password set. You can now sign in with Google or email and password.',
    passwordSetError: 'Failed to set password.',
    passwordWeakError: 'Password is too weak. Please choose a stronger password.',
    passwordAlreadyLinked: 'An email/password account is already linked to this account.',
  },
};

export default en;
